import React, { useState } from 'react';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../../shared/icons/Icons';
import { FormData, getCourtsByState, getCourtLocations } from '../../../shared/data/formData';
import { Button } from '../../../shared/components/ui/Button';
import { Select } from '../../../shared/components/ui/Select';
import { Input } from '../../../shared/components/ui/Input';
import { Card } from '../../../shared/components/ui/Card';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';
import { RadioCard } from '../../../shared/components/ui/RadioCard';
import { PageTitle } from '../../../shared/components/ui/PageTitle';
import { SectionTitle } from '../../../shared/components/ui/SectionTitle';
import { InfoBanner } from '../../../shared/components/ui/InfoBanner';

interface CustomerCourtFilingProps {
  onNavigate: (page: string) => void;
}

const CustomerCourtFiling: React.FC<CustomerCourtFilingProps> = ({ onNavigate }) => {
  const brand = useBrand();
  const [step, setStep] = useState(1);
  const [filingType, setFilingType] = useState('');

  const [jurisdiction, setJurisdiction] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCourt, setSelectedCourt] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  const courtOptions = jurisdiction === 'federal'
    ? FormData.federalCourts
    : (selectedState ? getCourtsByState(selectedState) : [{ value: '', label: 'Select state first...' }]);

  const locationOptions = selectedCourt
    ? getCourtLocations(selectedCourt)
    : [{ value: '', label: 'Select court first...' }];

  const handleJurisdictionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setJurisdiction(e.target.value);
    setSelectedCourt('');
    setSelectedLocation('');
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedState(e.target.value);
    setSelectedCourt('');
    setSelectedLocation('');
  };

  const handleCourtChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCourt(e.target.value);
    setSelectedLocation('');
  };

  return (
    <div>
      <PageTitle subtitle="Choose the type of filing to get started">Start a File &amp; Serve Order</PageTitle>

      <Card className="mb-6">
        <ChoiceGroup question="Filing type" hideLegend className="grid grid-cols-2 gap-4">
          <RadioCard
            name="filingType"
            label="Subsequent Filing"
            description="Select to file on an active case with a court-assigned case number."
            checked={filingType === 'subsequent'}
            onChange={() => { setFilingType('subsequent'); setStep(2); }}
          />
          <RadioCard
            name="filingType"
            label="Case Initiation"
            description="Select to initiate a new case. The clerk will assign a case number once documents are accepted."
            checked={filingType === 'initiation'}
            onChange={() => { setFilingType('initiation'); setStep(2); }}
          />
        </ChoiceGroup>
      </Card>

      {step >= 2 && filingType === 'subsequent' && (
        <Card className="mb-6">
          <SectionTitle>Look up case</SectionTitle>
          <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>If you previously filed on a case, you can search for it below.</p>
          <Input placeholder="Enter matter name, case number, or case title..." className="mb-3" />
          <a href="#" className="text-sm font-medium" style={{ color: brand.colors.link }}>Click here to search by court information</a>
        </Card>
      )}

      {step >= 2 && filingType === 'initiation' && (
        <Card className="mb-6">
          <SectionTitle>Court information</SectionTitle>
          <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>Enter the court information for the case you are initiating. Fields marked with an asterisk (*) are required.</p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Select
              label="Choose the subject matter jurisdiction"
              required
              options={FormData.jurisdictions}
              value={jurisdiction}
              onChange={handleJurisdictionChange}
            />
            <Select
              label="In which state would you like to file your document"
              required
              options={FormData.states}
              value={selectedState}
              onChange={handleStateChange}
              disabled={jurisdiction === 'federal'}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Select
              label="Choose the court"
              required
              options={courtOptions}
              value={selectedCourt}
              onChange={handleCourtChange}
            />
            <Select
              label="Choose the court location"
              required
              options={locationOptions}
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Select label="Case Category" required options={FormData.caseCategories} />
            <Select label="Filing Type" required options={FormData.filingTypes} />
          </div>
          <InfoBanner variant="warning">
            <span className="font-semibold">Not sure about the address or agent?</span> Before proceeding, consider using our <a href="#" style={{ color: brand.colors.link }} className="font-medium">Locate service</a> to ensure accuracy and increase the chances of successful service.
          </InfoBanner>
        </Card>
      )}

      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={() => onNavigate('dashboard')}>CANCEL ORDER</Button>
        <Button disabled={step < 2} icon={Icons.ArrowRight}>CONTINUE</Button>
      </div>
    </div>
  );
};

export default CustomerCourtFiling;
